'use client'

import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'

import GenreDeviceFilterModal from '@/app/[locale]/(worlds)/(worlds-landing)/search/components/GenreDeviceFilterModal'
import SortByMenu from '@/app/[locale]/(worlds)/(worlds-landing)/search/components/SortByMenu'
import ViverseButton from '@/components/ViverseButton'
import {
  BI_CATEGORY_SORT_AND_SEARCH,
  BI_EVENT_FEATURED,
  BI_EVENT_LATEST,
  BI_EVENT_MOST_LIKED,
  BI_EVENT_MOST_VISITED,
  BI_EVENT_RELEASE_DATE,
} from '@/constants/bi-sdk'
import { SEARCH_PARAM_KEYS } from '@/constants/search'
import { useBISdkContext } from '@/context/BISdkContext'
import { useSearchFilters } from '@/hooks/useSearchFilters'
import { usePathname } from '@/i18n/navigation'
import { cn } from '@/lib/utils'
import IconTool from '@/public/images/worlds/icon_tool.svg'
import { RoomTagWithCount } from '@/types/genre'
import { SortValue } from '@/types/search'

type SearchToolbarProps = {
  hidden?: boolean
  filterModalTopOffset?: number
  filterTags?: RoomTagWithCount[]
  hideType?: boolean
}

export default function SearchToolbar({
  hidden,
  filterModalTopOffset,
  filterTags = [],
  hideType = false,
}: SearchToolbarProps) {
  const t = useTranslations()
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const { sendBIReport } = useBISdkContext()

  const isSearchPage = pathname === '/search'

  const sortBy = (searchParams.get(SEARCH_PARAM_KEYS.SORT) || 'recommended') as SortValue

  const {
    showFilters,
    setShowFilters,
    device,
    tag,
    type,
    updateSearchParams,
    handleDeviceChange,
    handleFilterChange,
    handleTypeChange,
    handleClearAllFilters,
  } = useSearchFilters()

  const handleSortChange = (value: SortValue) => {
    const biEvents: Partial<Record<SortValue, string>> = {
      latest: BI_EVENT_LATEST,
      liked: BI_EVENT_MOST_LIKED,
      popular: BI_EVENT_MOST_VISITED,
      recommended: BI_EVENT_FEATURED,
      'release-date': BI_EVENT_RELEASE_DATE,
    }

    sendBIReport({
      ev: biEvents[value] || BI_EVENT_LATEST,
      category: BI_CATEGORY_SORT_AND_SEARCH,
    })

    updateSearchParams({ [SEARCH_PARAM_KEYS.SORT]: value })
  }

  return (
    <>
      <div
        data-component="explore-search-toolbar"
        className={cn('flex items-center gap-3 px-4 max-md:w-full md:pr-11 md:pl-6', {
          hidden: hidden,
        })}
      >
        <SortByMenu sortBy={sortBy} onSortChange={handleSortChange} />

        <ViverseButton
          variant="icon3"
          size="silent2"
          onClick={() => setShowFilters(true)}
          className={cn('flex min-w-fit items-center gap-2', {
            'bg-vive-primary-60 text-white': showFilters,
          })}
        >
          <IconTool className="size-4" />
          {t('worlds-landing.search.filters')}
        </ViverseButton>
      </div>

      <GenreDeviceFilterModal
        open={showFilters}
        onOpenChange={setShowFilters}
        device={device}
        tag={tag}
        type={type}
        hideType={hideType}
        onDeviceChange={handleDeviceChange}
        onFilterChange={handleFilterChange}
        onTypeChange={handleTypeChange}
        onClearAll={handleClearAllFilters}
        isSearchPage={isSearchPage}
        filterTags={filterTags}
        {...(Number(filterModalTopOffset) > 0 && {
          contentStyle: { marginTop: `${filterModalTopOffset}px` },
          contentClassName: `max-h-[calc(100dvh-${filterModalTopOffset}px)]`,
        })}
      />
    </>
  )
}
